import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ExternalLink, ArrowLeft, ChevronRight, Instagram, Phone, Globe, Music, Youtube, Facebook } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { PERFORMING_ARTISTS_DATA, toTypeArray } from './PerformingArtists';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&h=400&fit=crop';

// Same key function PerformingArtists.tsx uses for its managed collection, so
// the slug in the URL always matches the entry the grid card linked from.
function artistSlug(a: any, i: number) {
  return slugify(a.name || String(i));
}

// Collect every contact/social URL an artist has, deduped, with a label and
// icon — mirrors the button logic on the grid cards.
function collectContacts(a: any) {
  const candidates: { url?: string; label?: string }[] = [
    { url: a.contactUrl },
    { url: a.socialMediaUrl },
    { url: a.instagram, label: 'Instagram' },
    { url: a.facebook, label: 'Facebook' },
    { url: a.youtube, label: 'YouTube' },
    { url: a.tiktok, label: 'TikTok' },
    { url: a.website, label: 'Website' },
  ];
  const seen = new Set<string>();
  return candidates
    .filter((c): c is { url: string; label?: string } => !!c.url && c.url !== '#')
    .filter((c) => (seen.has(c.url) ? false : (seen.add(c.url), true)))
    .map((c) => {
      const u = c.url.toLowerCase();
      if (u.includes('facebook.com')) return { ...c, Icon: Facebook, label: 'Facebook' };
      if (u.includes('youtube.com') || u.includes('youtu.be')) return { ...c, Icon: Youtube, label: 'YouTube' };
      if (u.includes('instagram.com')) return { ...c, Icon: Instagram, label: 'Instagram' };
      return { ...c, Icon: Globe, label: c.label || 'Website' };
    });
}

export default function PerformingArtistDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('performing_artists', PERFORMING_ARTISTS_DATA, (a, i) =>
    artistSlug(a, i)
  );

  const entry = mc.items.find((a: any, i: number) => artistSlug(a, i) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Artist Not Found | Dallas Desi Scoop"
          description="This performing artist listing could not be found."
          canonical="/performing-artists"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Performing artist not found.</p>
        <Link
          to="/performing-artists"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Performing Artists
        </Link>
      </div>
    );
  }

  const types = toTypeArray(entry.type);
  const typeLabel = types.length > 0 ? types.join(' & ') : 'Performing Artist';
  const shortDesc = (entry.description || '').trim();
  const rawLocation = (entry.location || '').trim();
  const cityLabel = rawLocation
    ? rawLocation.includes('TX')
      ? rawLocation
      : `${rawLocation}, TX`
    : 'Dallas-Fort Worth';

  const contacts = collectContacts(entry);
  const phone = (entry.phone || '').trim();
  const canonicalPath = `/performing-artists/${slug}`;

  const title = `${entry.name} | ${typeLabel} in ${cityLabel} | Dallas Desi Scoop`;
  const metaDescription = `${entry.name} is a ${typeLabel.toLowerCase()} in ${cityLabel} serving the South Asian community across the Dallas-Fort Worth metroplex. Find contact details and more on Dallas Desi Scoop.`;

  const jsonLd = [
    {
      '@type': 'PerformingGroup',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: `${entry.name}, ${typeLabel} in ${cityLabel}, serving the Dallas-Fort Worth Desi community.`,
      telephone: phone || undefined,
      address: rawLocation
        ? {
            '@type': 'PostalAddress',
            addressLocality: rawLocation,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : undefined,
      url: contacts[0]?.url || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Performing Artists', item: `${BASE_URL}/performing-artists` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other artists of the same type first.
  const relatedArtists = mc.items
    .map((a: any, i: number) => ({ artist: a, key: artistSlug(a, i) }))
    .filter((r: any) => r.key !== slug)
    .sort(
      (a: any, b: any) =>
        Number(toTypeArray(b.artist.type).some((t: string) => types.includes(t))) -
        Number(toTypeArray(a.artist.type).some((t: string) => types.includes(t)))
    )
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/performing-artists"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Performing Artists
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <Music className="w-3.5 h-3.5 text-[#51ba4f]" />
              {typeLabel}
            </span>
            {rawLocation && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {cityLabel}
              </span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.name} is a {typeLabel.toLowerCase()} serving the South Asian community in {cityLabel} and across the Dallas-Fort Worth metroplex.
            {shortDesc ? ` ${shortDesc}.` : ''}
            {` Contact ${entry.name} directly for bookings, pricing, and availability for weddings, cultural events, and celebrations.`}
          </p>
        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {contacts.map((c) => (
              <a
                key={c.url}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <c.Icon className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{c.label}</span>
              </a>
            ))}
            {phone && (
              <a
                href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{phone}</span>
              </a>
            )}
            {contacts.length === 0 && !phone && (
              <p className="text-gray-400 text-[13px]">Contact information coming soon.</p>
            )}
          </div>
          {contacts.length > 0 && (
            <a
              href={contacts[0].url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Contact {entry.name}
            </a>
          )}
        </div>
      </div>

      {/* Internal links: related artists */}
      {relatedArtists.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">More Performing Artists in DFW</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {relatedArtists.map(({ artist, key }: any) => (
              <Link
                key={key}
                to={`/performing-artists/${key}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <Music className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {artist.name}
                <span className="ml-auto text-gray-400 text-[11px]">
                  {toTypeArray(artist.type)[0] || 'Artist'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/performing-artists', label: 'All Performing Artists in DFW' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/party-venues', label: 'Party Venues' },
            { to: '/restaurants', label: 'Desi Restaurants' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="bg-white border border-gray-100 rounded-full px-4 py-2 text-[12px] font-medium text-gray-600 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
